import React, { Component } from 'react';
import { Input, Tabs } from 'antd';
import moment from 'moment';
import { Tenant } from 'models/Blip/Index';
import { OverviewRequest } from 'models/request/OverviewRequest';
import { OverviewResponse } from 'models/response/OverviewResponse';
import { PushNotification } from 'utils/PushNotification';
import { PermissionService } from 'services/PermissionService';
import { Permissions } from 'permissions';
import { calculateActiveCampaign, getOverviewInfo, getActiveCampaignsStatus } from 'services/CeberusApiService';
import { getActiveCampaignsDeliveryReport } from 'services/CeberusApiService';
import { ActiveCampaignRequest } from 'models/request/ActiveCampaignRequest';
import { ActiveCampaignDeliveryReportRequest } from 'models/request/ActiveCampaignDeliveryReportRequest';
import { SubscribedAppsResponse } from 'models/response/Index';
import ActiveCampaignHistory from 'components/ActiveCampaign/History';

const { TabPane } = Tabs;

const FORMATIME = 'HH:mm:ss';

interface CampaignState {
  isLoading: boolean;
  isLoadingButtonSearch: boolean;
  overviewResponse?: OverviewResponse;
  activeCampaignResponse: any;
  subscribedApps: SubscribedAppsResponse[];
  appIdExists: boolean;
  tenantSelected: Tenant;
  hasEditPermission: boolean;
  hasReadPermission: boolean;
  formValues: {
    volumeMessages: number;
    startDate: moment.Moment | null;
    startTime: moment.Moment | null;
    endDate: moment.Moment | null;
    endTime: moment.Moment | null;
    campaignType?: string;
  };
  formErrors: { [key: string]: string };
  historyLoading: boolean;
  historyItems: any[];
  historyLoaded: boolean;
    campaignType?: string;
}

import ActiveCampaignCalculator from 'components/ActiveCampaign/Calculator';

export class Campaign extends Component<OverviewRequest, CampaignState> {
  state: CampaignState = {
    isLoading: false,
    isLoadingButtonSearch: false,
    overviewResponse: undefined,
    activeCampaignResponse: null,
    subscribedApps: [],
    appIdExists: false,
    tenantSelected: new Tenant(),
    hasEditPermission: PermissionService.hasPermission(
      Permissions.Application_Overview__Edit
    ),
    hasReadPermission: PermissionService.hasPermission(
      Permissions.Application_Overview__Read
    ),
    formValues: {
      volumeMessages: 0,
      startDate: moment(),
      startTime: moment('00:00:00', FORMATIME),
      endDate: moment(),
      endTime: moment('00:00:00', FORMATIME),
      campaignType: 'batch',
    },
    formErrors: {},
    historyLoading: false,
    historyItems: [],
    historyLoaded: false,
  };

  componentDidMount() {
    if (this.props.BotIdentifier) {
      this.loadHistory();
    }
  }

  componentDidUpdate(prevProps: OverviewRequest) {
    if (prevProps.BotIdentifier !== this.props.BotIdentifier && this.props.BotIdentifier) {
      this.onSearch(this.props.BotIdentifier as any);
    }
  }

  loadHistory = async (force: boolean = false) => {
    if (this.state.historyLoaded && !force) return;

    try {
      this.setState({ historyLoading: true });
      const request = new ActiveCampaignRequest();
      request.BotIdentifier = this.props.BotIdentifier;
      request.ClusterName = this.props.ClusterName || this.state.tenantSelected.name;
      request.CampaignType = this.state.formValues.campaignType;
      const data = await getActiveCampaignsStatus(request);
      this.setState({ historyItems: data.items || [], historyLoaded: true });
    } catch (error) {
      PushNotification.httpError(error);
    } finally {
      this.setState({ historyLoading: false });
    }
  };

  downloadDeliveryReport = async (item: any) => {
    try {
      this.setState({ historyLoading: true });
      const request = new ActiveCampaignDeliveryReportRequest();
      request.BotIdentifier = this.props.BotIdentifier;
      request.ClusterName = this.props.ClusterName || this.state.tenantSelected.name;
      request.CampaignId = item.id;

      const data = await getActiveCampaignsDeliveryReport(request);
      const downloadUrl = data && data.downloadUrl;
      if (!downloadUrl) {
        PushNotification.error('No report available for download.');
        return;
      }

      const win = window.open(downloadUrl, '_blank');
      if (!win) {
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (error) {
      PushNotification.httpError(error);
    } finally {
      this.setState({ historyLoading: false });
    }
  };

  

  onSearch = async (identifier: string) => {
    const trimmed = (identifier || '').trim();
    if (!trimmed) return;

    try {
      this.setState({ isLoading: true, isLoadingButtonSearch: true });

      const request = new OverviewRequest();
      request.ClusterName = this.props.ClusterName || this.state.tenantSelected.name;
      request.BotIdentifier = trimmed;

      const overviewResponse = await getOverviewInfo(request);
      this.setState({ overviewResponse });
      this.loadHistory();
    } catch (error) {
      PushNotification.httpError(error);
    } finally {
      this.setState({ isLoading: false, isLoadingButtonSearch: false });
    }
  };

  handleSubmit = async (e?: any) => {
    if (e && e.preventDefault) e.preventDefault();

    try {
      this.setState({ isLoading: true });

      const {
        volumeMessages,
        startDate,
        startTime,
        endDate,
        endTime,
      } = this.state.formValues;

      const request = new ActiveCampaignRequest();
      request.BotIdentifier = this.props.BotIdentifier;
      request.ClusterName = this.props.ClusterName || this.state.tenantSelected.name;
      request.VolumeMessages = volumeMessages;
      request.CampaignType = this.state.formValues.campaignType;

      if (startDate && startTime) {
        request.StartDateTime = moment(startDate)
          .set({
            hour: startTime.hour(),
            minute: startTime.minute(),
            second: startTime.second(),
          })
          .toDate();
      }

      if (endDate && endTime) {
        request.EndDateTime = moment(endDate)
          .set({
            hour: endTime.hour(),
            minute: endTime.minute(),
            second: endTime.second(),
          })
          .toDate();
      }

      const activeCampaignResponse = await calculateActiveCampaign(request);
      this.setState({ activeCampaignResponse });
    } catch (error) {
      PushNotification.httpError(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  updateField = (field: string, value: any) => {
    this.setState(prev => ({
      formValues: {
        ...prev.formValues,
        [field]: value,
      },
    }));
  };

  render() {
    const {activeCampaignResponse, formValues } =
      this.state;

    return (
      <Tabs defaultActiveKey="1" className="w-100" onChange={(activeKey) => { if (activeKey === '1') { this.loadHistory(); } }}>
        <TabPane tab="History" key="1">
          <ActiveCampaignHistory
            historyItems={this.state.historyItems}
            historyLoading={this.state.historyLoading}
            formValues={formValues}
            onChangeCampaignType={(v: string) => this.setState(prev => ({ formValues: { ...prev.formValues, campaignType: v }, historyLoaded: false }), () => this.loadHistory(true))}
            loadHistory={(force?: boolean) => this.loadHistory(force)}
            downloadDeliveryReport={(item: any) => this.downloadDeliveryReport(item)}
            canDownload={this.state.hasEditPermission}
          />
        </TabPane>
        <TabPane tab="Calculator" key="2">
          <ActiveCampaignCalculator
            formValues={formValues}
            updateField={this.updateField}
            handleSubmit={this.handleSubmit}
            activeCampaignResponse={activeCampaignResponse}
          />
        </TabPane>
      </Tabs>
    );
  }
}